import React, { useState, useRef, useMemo, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    Chip,
    TextField,
    LinearProgress,
    FormControlLabel,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import timesheetService from '../../../services/timesheetService';
import SignSelectItemsSection from './SignSelectItemsSection';
import SignDayNarrativesSection from './SignDayNarrativesSection';
import SignForemanCommentSection from './SignForemanCommentSection';
import SelectEmployeesAndTempsModal from '../../jha/SelectEmployeesAndTempsModal';

function getWeekdayShort(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()];
}

function formatShortDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    return `${date.getMonth() + 1}/${date.getDate()}`;
}

// Имя залогиненного пользователя (foreman по умолчанию) — как в EmployeeWarningNoticeForm.
function getLoggedInName() {
    const f = localStorage.getItem('firstName') || '';
    const l = localStorage.getItem('lastName') || '';
    return `${f} ${l}`.trim();
}

export default function SignTimesheetModal({
    open,
    onClose,
    jobs,
    employees,
    weekRecords,
    weekDailyNotes,
    weekInfo,
    currentDate,
    onSign,
}) {
    // Mode: 'select' (choose jobs) or 'sign' (signing per customer)
    const [mode, setMode] = useState('select');
    const [selectedJobIds, setSelectedJobIds] = useState(new Set());
    const [currentCustomerIndex, setCurrentCustomerIndex] = useState(0);
    const [signerNameByCustomer, setSignerNameByCustomer] = useState({}); // { [idx]: имя заказчика } (необязательное)
    const [foremanName, setForemanName] = useState(''); // foreman по умолчанию = залогиненный
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [sigTarget, setSigTarget] = useState(null); // 'foreman' | 'customer' — чью подпись рисуем
    const [foremanPickerOpen, setForemanPickerOpen] = useState(false); // выбор форемана-подписанта
    const [signing, setSigning] = useState(false);
    // "Email me the PDF" — по галочке письмо с фирменным PDF уходит подписавшему
    // на его email. Выбор запоминаем между подписями.
    const [emailPdf, setEmailPdf] = useState(() => {
        try { return localStorage.getItem('timesheetEmailPdfOnSign') === '1'; } catch { return false; }
    });
    const toggleEmailPdf = (checked) => {
        setEmailPdf(checked);
        try { localStorage.setItem('timesheetEmailPdfOnSign', checked ? '1' : '0'); } catch { /* noop */ }
    };
    // Какой PDF прислать: только подписанный день или всю неделю.
    const [emailPdfScope, setEmailPdfScope] = useState(() => {
        try { return localStorage.getItem('timesheetEmailPdfScope') === 'week' ? 'week' : 'day'; } catch { return 'day'; }
    });
    const pickEmailPdfScope = (scope) => {
        setEmailPdfScope(scope);
        try { localStorage.setItem('timesheetEmailPdfScope', scope); } catch { /* noop */ }
    };
    const [frozenCustomers, setFrozenCustomers] = useState([]);
    const sigPadRef = useRef(null);

    // Per-customer state для выбора Material/Equipment/Subcontractor POs и комментария.
    // Хранится по индексу customer'а — при back/next не теряется.
    const [poSelectionsByCustomer, setPoSelectionsByCustomer] = useState({}); // { [idx]: Set<id> }
    const [commentByCustomer, setCommentByCustomer] = useState({}); // { [idx]: string }
    const [resolvedPosByCustomer, setResolvedPosByCustomer] = useState({}); // { [idx]: po-объекты }
    const [itemNotesByCustomer, setItemNotesByCustomer] = useState({}); // { [idx]: { [poId]: note } }
    const [narrativesByCustomer, setNarrativesByCustomer] = useState({}); // { [idx]: {workPerformance,delays,extraWork} }
    const [foremanSigByCustomer, setForemanSigByCustomer] = useState({}); // { [idx]: dataURL }
    const [customerSigByCustomer, setCustomerSigByCustomer] = useState({}); // { [idx]: dataURL }
    const [resignByCustomer, setResignByCustomer] = useState({}); // { [idx]: reason } — нужна переподпись

    const weekDates = useMemo(() => timesheetService.getWeekDates(currentDate), [currentDate]);

    // Подписывается открытый день: вся неделя видна, а подпись ложится на него.
    const signDay = currentDate;
    const dayRecords = useCallback((d, jobId) => weekRecords.filter(r =>
        r.workDate === d && !r.isDeleted && (r.hours > 0 || r.attendCode)
        && (jobId == null || r.jobId === jobId)), [weekRecords]);
    const isDaySigned = useCallback((d, jobId) => {
        const recs = dayRecords(d, jobId);
        return recs.length > 0 && recs.every(r => ['signed', 'submitted', 'approved'].includes(r.status));
    }, [dayRecords]);
    const dayTitle = (d) => `${getWeekdayShort(d)} ${formatShortDate(d)}`;

    // Reset state when modal opens
    React.useEffect(() => {
        if (open) {
            setMode('select');
            setSelectedJobIds(new Set());
            setCurrentCustomerIndex(0);
            setSignerNameByCustomer({});
            setForemanName(getLoggedInName());
            setSigTarget(null);
            setSigning(false);
            setFrozenCustomers([]);
            setPoSelectionsByCustomer({});
            setCommentByCustomer({});
            setResolvedPosByCustomer({});
            setItemNotesByCustomer({});
            setNarrativesByCustomer({});
            setForemanSigByCustomer({});
            setCustomerSigByCustomer({});
            setResignByCustomer({});
        }
    }, [open]);

    // Group jobs by customer
    const customerGroups = useMemo(() => {
        const groups = {};
        for (const job of jobs) {
            // Только работы с часами в выбранный день
            if (dayRecords(signDay, job.id).length === 0) continue;
            const customer = timesheetService.getJobCustomer(job) || 'Other';
            if (!groups[customer]) groups[customer] = [];
            groups[customer].push(job);
        }
        return groups;
    }, [jobs, dayRecords, signDay]);

    // Build hours per job (employees x days)
    const jobSummaries = useMemo(() => {
        const summaries = {};
        for (const job of jobs) {
            const jobRecords = weekRecords.filter(r => r.jobId === job.id && !r.isDeleted);
            const employeeIds = [...new Set(jobRecords.map(r => r.employeeId))];

            const empList = employeeIds.map(empId => {
                const emp = employees.find(e => e.id === empId);
                const dayHours = {};
                let total = 0;
                for (const d of weekDates) {
                    const rec = jobRecords.find(r => r.employeeId === empId && r.workDate === d);
                    const h = rec?.hours || 0;
                    dayHours[d] = h;
                    total += h;
                }
                return {
                    id: empId,
                    name: timesheetService.getEmployeeName(emp),
                    dayHours,
                    total,
                };
            }).filter(e => e.total > 0).sort((a, b) => a.name.localeCompare(b.name));

            const dailyNotes = (weekDailyNotes || []).filter(n => n.jobId === job.id && !n.employeeId && n.notes);

            const empNotes = [];
            for (const rec of jobRecords) {
                if (rec.notes) {
                    const emp = employees.find(e => e.id === rec.employeeId);
                    empNotes.push({
                        employeeName: timesheetService.getEmployeeName(emp),
                        date: rec.workDate,
                        notes: rec.notes,
                    });
                }
            }

            summaries[job.id] = {
                employees: empList,
                totalHours: empList.reduce((sum, e) => sum + e.total, 0),
                employeeCount: empList.length,
                dailyNotes,
                employeeNotes: empNotes,
            };
        }
        return summaries;
    }, [jobs, weekRecords, weekDailyNotes, employees, weekDates]);

    const isJobSigned = (jobId) => isDaySigned(signDay, jobId);

    const toggleJobSelection = (jobId) => {
        setSelectedJobIds(prev => {
            const next = new Set(prev);
            if (next.has(jobId)) next.delete(jobId);
            else next.add(jobId);
            return next;
        });
    };

    // Customers to sign (only those with selected jobs)
    const customersToSign = useMemo(() => {
        const list = [];
        for (const [customer, customerJobs] of Object.entries(customerGroups)) {
            const selected = customerJobs.filter(j => selectedJobIds.has(j.id));
            if (selected.length > 0) {
                list.push({ customer, jobs: selected });
            }
        }
        return list;
    }, [customerGroups, selectedJobIds]);

    const startSigning = async () => {
        if (customersToSign.length === 0) return;
        setFrozenCustomers(customersToSign);
        setCurrentCustomerIndex(0);
        setMode('sign');

        // Подгружаем ранее введённое, чтобы повторная подпись (после правки часов,
        // которая сбрасывает подпись) НЕ теряла добавленное. Читаем все signed-записи
        // недели (в т.ч. soft-deleted invalidated) и берём самую свежую по каждой работе.
        try {
            const weekEnd = timesheetService.getWeekEndingDate(currentDate);
            const all = await timesheetService.getAllSignedTimesheets();
            const weekSigned = all.filter(s => s.weekEndingDate === weekEnd);
            if (weekSigned.length === 0) return;

            const poSel = {}, notes = {}, comments = {}, narrs = {}, fSigs = {}, cSigs = {}, signers = {}, resign = {};
            customersToSign.forEach((group, idx) => {
                const jobIdSet = new Set(group.jobs.map(j => j.id));
                const jobRecs = weekSigned.filter(s => jobIdSet.has(s.jobId));
                const rec = [...jobRecs]
                    .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))[0];
                // Нужна переподпись, если у любой работы заказчика стоит флаг.
                const needing = jobRecs.find(s => s.needsResign);
                if (needing) resign[idx] = needing.resignReason || 'Signature cleared — re-sign required';
                if (!rec) return;
                if (Array.isArray(rec.items) && rec.items.length) {
                    const ids = new Set();
                    for (const it of rec.items) {
                        if (it.poId != null) {
                            ids.add(it.poId);
                            if (it.note) notes[idx] = { ...(notes[idx] || {}), [it.poId]: it.note };
                        }
                    }
                    if (ids.size) poSel[idx] = ids;
                }
                if (rec.foremanComment) comments[idx] = rec.foremanComment;
                if (rec.dayNarratives && Object.keys(rec.dayNarratives).length) narrs[idx] = rec.dayNarratives;
                // Подписи (форемана и клиента) не подставляем: каждая подпись —
                // заново, иначе прошлая картинка уходила как новая подпись.
                if (rec.signerName) signers[idx] = rec.signerName;
            });
            setPoSelectionsByCustomer(poSel);
            setItemNotesByCustomer(notes);
            setCommentByCustomer(comments);
            setNarrativesByCustomer(narrs);
            setForemanSigByCustomer(fSigs);
            setCustomerSigByCustomer(cSigs);
            setSignerNameByCustomer(signers);
            setResignByCustomer(resign);
        } catch (e) {
            console.error('[Sign] preload existing signed data failed', e);
        }
    };

    const handleBack = () => {
        if (currentCustomerIndex > 0) {
            setCurrentCustomerIndex(prev => prev - 1);
        } else {
            setMode('select');
            setFrozenCustomers([]);
        }
    };

    const openForemanSignature = () => { setSigTarget('foreman'); setSignatureModalOpen(true); };
    const openCustomerSignature = () => { setSigTarget('customer'); setSignatureModalOpen(true); };

    const removeCustomerSignature = () => {
        setCustomerSigByCustomer(prev => {
            const next = { ...prev };
            delete next[currentCustomerIndex];
            return next;
        });
    };

    // Модалка подписи только ЗАХВАТЫВАЕТ рисунок в нужный слот (foreman/customer).
    // Само подписание — отдельной кнопкой (handleComplete).
    const handleSignatureAccept = () => {
        if (!sigPadRef.current) return;
        const img = sigPadRef.current.toDataURL();
        if (sigTarget === 'foreman') {
            setForemanSigByCustomer(prev => ({ ...prev, [currentCustomerIndex]: img }));
        } else if (sigTarget === 'customer') {
            setCustomerSigByCustomer(prev => ({ ...prev, [currentCustomerIndex]: img }));
        }
        setSignatureModalOpen(false);
        setSigTarget(null);
    };

    // Завершение подписи текущего customer'а: собираем payload и отдаём в onSign.
    const handleComplete = async () => {
        const currentGroup = frozenCustomers[currentCustomerIndex];
        if (!currentGroup) return;
        const idx = currentCustomerIndex;
        const jobIds = currentGroup.jobs.map(j => j.id);

        // Подпись форемана (нарисованная, опц.) + заказчика (опц.). Имя форемана
        // по умолчанию = залогиненный; подпись не авто-генерируется.
        const foremanSignatureImage = foremanSigByCustomer[idx] || '';
        const customerSignatureImage = customerSigByCustomer[idx] || '';

        // items + per-item note (Description). estAmount/estUnitAmount — cost snapshot.
        const resolvedPos = resolvedPosByCustomer[idx] || [];
        const notes = itemNotesByCustomer[idx] || {};
        const items = resolvedPos.map(po => {
            const estAmount = po.estAmount != null ? Number(po.estAmount) : null;
            const estUnitAmount = po.estUnitAmount != null ? Number(po.estUnitAmount) : null;
            const note = (notes[po.id] || '').trim();
            return {
                poId: po.id,
                externalId: po.externalId ?? null,
                po: po._po,
                vendor: po._vendor,
                description: po._description,
                note: note || null,
                costCode: po._costCode,
                category: po._category,
                job: po._job,
                equipment: po._equipment,
                location: po._location,
                estAmount: Number.isFinite(estAmount) ? estAmount : null,
                estUnitAmount: Number.isFinite(estUnitAmount) ? estUnitAmount : null,
            };
        });
        const foremanComment = commentByCustomer[idx] || '';
        const rawNarr = narrativesByCustomer[idx];
        const dayNarratives = rawNarr && Object.keys(rawNarr).length ? rawNarr : null;

        setSigning(true);
        try {
            await onSign(jobIds, currentGroup.customer, {
                foremanSignatureImage,
                foremanName: foremanName || '',
                customerSignatureImage,
                signerName: signerNameByCustomer[idx] || '',
                items,
                foremanComment,
                dayNarratives,
                signedDay: signDay,
                emailPdfScope,
                emailPdf, // галочка "Email me the PDF" — сервер шлёт письмо подписавшему
            });

            // Move to next customer or finish
            if (currentCustomerIndex < frozenCustomers.length - 1) {
                setCurrentCustomerIndex(prev => prev + 1);
            } else {
                onClose();
            }
        } finally {
            setSigning(false);
        }
    };

    const currentGroup = mode === 'sign' ? frozenCustomers[currentCustomerIndex] : null;

    // Render hours table for a job
    const renderJobTable = (job) => {
        const summary = jobSummaries[job.id];
        if (!summary || summary.totalHours === 0) return null;

        const jobCode = timesheetService.getJobCode(job);
        const jobName = timesheetService.getJobName(job);
        const customer = timesheetService.getJobCustomer(job);

        return (
            <Box key={job.id} sx={{ mb: 2 }}>
                {/* Job header — code + customer + name. Заметно, чтобы при подписи
                    foreman видел КАКУЮ работу подписывает (а не угадывал по description). */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Chip
                        label={`#${jobCode}`}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 700, height: 22 }}
                    />
                    {customer && (
                        <Chip
                            label={customer}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 600, height: 22 }}
                        />
                    )}
                    <Typography variant="subtitle2" fontWeight={600}>
                        {jobName}
                    </Typography>
                    <Typography component="span" variant="body2" color="text.secondary">
                        {summary.totalHours}h — {summary.employeeCount} {summary.employeeCount === 1 ? 'employee' : 'employees'}
                    </Typography>
                </Box>

                <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                        <thead>
                            <tr>
                                <Box component="th" sx={{
                                    textAlign: 'left', py: 0.5, px: 1, fontWeight: 600,
                                    borderBottom: '2px solid', borderColor: 'divider',
                                    position: 'sticky', left: 0, bgcolor: 'background.paper', minWidth: 100,
                                }}>
                                    Employee
                                </Box>
                                {weekDates.map(d => (
                                    <Box component="th" key={d} sx={{
                                        textAlign: 'center', py: 0.5, px: 0.5, fontWeight: 600,
                                        borderBottom: '2px solid', borderColor: 'divider', minWidth: 40,
                                        ...(d === signDay ? { bgcolor: '#ecfdf5', color: 'success.dark' } : {}),
                                    }}>
                                        <Box>{getWeekdayShort(d)}</Box>
                                        <Box sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{formatShortDate(d)}</Box>
                                    </Box>
                                ))}
                                <Box component="th" sx={{
                                    textAlign: 'center', py: 0.5, px: 0.5, fontWeight: 700,
                                    borderBottom: '2px solid', borderColor: 'divider', color: 'success.dark',
                                }}>
                                    Total
                                </Box>
                            </tr>
                        </thead>
                        <tbody>
                            {summary.employees.map((emp, idx) => (
                                <tr key={emp.id}>
                                    <Box component="td" sx={{
                                        py: 0.5, px: 1, whiteSpace: 'nowrap',
                                        position: 'sticky', left: 0,
                                        bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                                        borderBottom: '1px solid', borderColor: 'divider',
                                    }}>
                                        {emp.name}
                                    </Box>
                                    {weekDates.map(d => (
                                        <Box component="td" key={d} sx={{
                                            textAlign: 'center', py: 0.5, px: 0.5,
                                            bgcolor: d === signDay ? '#ecfdf5' : (idx % 2 === 0 ? 'background.paper' : 'grey.50'),
                                            borderBottom: '1px solid', borderColor: 'divider',
                                            color: emp.dayHours[d] > 0 ? 'text.primary' : 'text.disabled',
                                        }}>
                                            {emp.dayHours[d] > 0 ? emp.dayHours[d] : ''}
                                        </Box>
                                    ))}
                                    <Box component="td" sx={{
                                        textAlign: 'center', py: 0.5, px: 0.5, fontWeight: 700,
                                        color: 'success.dark',
                                        bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                                        borderBottom: '1px solid', borderColor: 'divider',
                                    }}>
                                        {emp.total}
                                    </Box>
                                </tr>
                            ))}
                            <tr>
                                <Box component="td" sx={{
                                    py: 0.5, px: 1, fontWeight: 700,
                                    borderTop: '2px solid', borderColor: 'divider',
                                    position: 'sticky', left: 0, bgcolor: 'background.paper',
                                }}>
                                    Total
                                </Box>
                                {weekDates.map(d => {
                                    const dayTotal = summary.employees.reduce((sum, e) => sum + (e.dayHours[d] || 0), 0);
                                    return (
                                        <Box component="td" key={d} sx={{
                                            textAlign: 'center', py: 0.5, px: 0.5, fontWeight: 700,
                                            borderTop: '2px solid', borderColor: 'divider',
                                            color: dayTotal > 0 ? 'warning.dark' : 'text.disabled',
                                        }}>
                                            {dayTotal > 0 ? dayTotal : ''}
                                        </Box>
                                    );
                                })}
                                <Box component="td" sx={{
                                    textAlign: 'center', py: 0.5, px: 0.5, fontWeight: 700,
                                    borderTop: '2px solid', borderColor: 'divider',
                                    color: 'success.dark',
                                }}>
                                    {summary.totalHours}
                                </Box>
                            </tr>
                        </tbody>
                    </table>
                </Box>

                {/* Daily notes */}
                {summary.dailyNotes.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">Daily Notes</Typography>
                        {summary.dailyNotes.map((n, idx) => (
                            <Box key={idx} sx={{ ml: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                <strong>{n.noteDate}:</strong> {n.notes}
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Employee notes */}
                {summary.employeeNotes.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">Employee Notes</Typography>
                        {summary.employeeNotes.map((n, idx) => (
                            <Box key={idx} sx={{ ml: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                <strong>{n.employeeName}</strong> ({n.date}): {n.notes}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {mode === 'select' ? (
                        `Sign ${dayTitle(signDay)} — Week ${weekInfo?.weekNumber || ''}`
                    ) : (() => {
                        const codes = (currentGroup?.jobs || [])
                            .map(j => `#${timesheetService.getJobCode(j)}`)
                            .filter(c => c !== '#')
                            .join(', ');
                        return `Signing: ${codes ? codes + ' — ' : ''}${currentGroup?.customer || ''} (${currentCustomerIndex + 1} of ${frozenCustomers.length})`;
                    })()}
                    {mode === 'sign' && (
                        <LinearProgress
                            variant="determinate"
                            value={((currentCustomerIndex + 1) / frozenCustomers.length) * 100}
                            sx={{ mt: 1, borderRadius: 1 }}
                        />
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {mode === 'select' ? (
                        /* ── Selection mode ── */
                        <>
                            {Object.keys(customerGroups).length === 0 && (
                                <Typography color="text.secondary">No hours on {dayTitle(signDay)}. Open the day you want to sign.</Typography>
                            )}
                            {Object.entries(customerGroups).map(([customer, customerJobs]) => {
                                const allSigned = customerJobs.every(j => isJobSigned(j.id));

                                return (
                                    <Accordion key={customer} defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                <Typography fontWeight={600}>{customer}</Typography>
                                                {allSigned && (
                                                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20, ml: 'auto', mr: 1 }} />
                                                )}
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {customerJobs.map((job) => {
                                                const summary = jobSummaries[job.id];
                                                const signed = isJobSigned(job.id);
                                                const isSelected = selectedJobIds.has(job.id);

                                                return (
                                                    <Box
                                                        key={job.id}
                                                        sx={{
                                                            display: 'flex', alignItems: 'center', gap: 1,
                                                            py: 0.5, px: 1, borderRadius: 1,
                                                            '&:hover': { bgcolor: 'action.hover' },
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={() => toggleJobSelection(job.id)}
                                                    >
                                                        <Checkbox
                                                            checked={isSelected}
                                                            size="small"
                                                            sx={{ p: 0.5 }}
                                                        />
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                                                    #{timesheetService.getJobCode(job)}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {timesheetService.getJobName(job)}
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {summary?.totalHours || 0}h — {summary?.employeeCount || 0} {(summary?.employeeCount || 0) === 1 ? 'employee' : 'employees'}
                                                            </Typography>
                                                        </Box>
                                                        {signed && (
                                                            <Chip label="Signed" size="small" color="success" variant="outlined" />
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                        </>
                    ) : (
                        /* ── Signing mode — show current customer's jobs with details ── */
                        <>
                            {resignByCustomer[currentCustomerIndex] && (
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, p: 1,
                                    borderRadius: 1, bgcolor: 'warning.light', border: '1px solid', borderColor: 'warning.main',
                                }}>
                                    <WarningIcon sx={{ color: 'warning.dark', fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="body2" fontWeight={700} color="warning.dark">Re-sign required</Typography>
                                        <Typography variant="caption" color="text.secondary">{resignByCustomer[currentCustomerIndex]}</Typography>
                                    </Box>
                                </Box>
                            )}
                            <Box sx={{ mb: 1.5, p: 1, borderRadius: 1, bgcolor: '#ecfdf5', border: '1px solid', borderColor: 'success.light' }}>
                                <Typography variant="body2" fontWeight={700}>Signing {dayTitle(signDay)} only</Typography>
                                <Typography variant="caption" color="text.secondary">The whole week is shown for reference. Each day is signed separately.</Typography>
                            </Box>
                            {currentGroup && currentGroup.jobs.map(job => renderJobTable(job))}

                            {/* Grand total if multiple jobs */}
                            {currentGroup && currentGroup.jobs.length > 1 && (
                                <Box sx={{ mt: 1, mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Chip
                                        label={`Grand Total: ${currentGroup.jobs.reduce((sum, j) => sum + (jobSummaries[j.id]?.totalHours || 0), 0)}h`}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontWeight: 700 }}
                                    />
                                </Box>
                            )}

                            {/* ── Опциональные детали в сворачиваемых секциях — свёрнуты по
                                   умолчанию, чтобы на iPad не было бесконечной простыни. В
                                   заголовке — счётчик, чтобы видеть что заполнено не разворачивая. ── */}
                            {currentGroup && (() => {
                                const idx = currentCustomerIndex;
                                // У кода работы (7888-1) PO — от его работы (7888).
                                const jobCodes = [...new Set(currentGroup.jobs
                                    .flatMap(j => [timesheetService.getJobCode(j), j.parentJob])
                                    .filter(Boolean))];
                                const selectedIds = poSelectionsByCustomer[idx] || new Set();
                                const comment = commentByCustomer[idx] || '';
                                const narr = narrativesByCustomer[idx] || {};
                                const narrCount = ['workPerformance', 'delays', 'extraWork'].reduce((n, k) => {
                                    const m = narr[k] || {};
                                    return n + Object.values(m).filter(v => (v || '').trim()).length;
                                }, 0);
                                const commentFilled = (comment || '').trim().length > 0;
                                const accSx = {
                                    border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1,
                                    boxShadow: 'none', '&:before': { display: 'none' },
                                };
                                const sumSx = { minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.75 } };
                                return (
                                    <Box sx={{ mt: 2 }}>
                                        <Accordion disableGutters elevation={0} sx={accSx}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sumSx}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" fontWeight={600}>Materials, Equipment &amp; Subcontractors</Typography>
                                                    {selectedIds.size > 0 && (
                                                        <Chip size="small" color="primary" label={`${selectedIds.size} selected`} sx={{ height: 20 }} />
                                                    )}
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ pt: 0 }}>
                                                <SignSelectItemsSection
                                                    jobCodes={jobCodes}
                                                    selectedIds={selectedIds}
                                                    onSelectedChange={(next) => {
                                                        setPoSelectionsByCustomer(prev => ({ ...prev, [idx]: next }));
                                                    }}
                                                    onSelectedPosResolved={(pos) => {
                                                        setResolvedPosByCustomer(prev => ({ ...prev, [idx]: pos }));
                                                    }}
                                                    itemNotes={itemNotesByCustomer[idx] || {}}
                                                    onItemNoteChange={(poId, text) => {
                                                        setItemNotesByCustomer(prev => ({
                                                            ...prev,
                                                            [idx]: { ...(prev[idx] || {}), [poId]: text },
                                                        }));
                                                    }}
                                                />
                                            </AccordionDetails>
                                        </Accordion>

                                        <Accordion disableGutters elevation={0} sx={accSx}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sumSx}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" fontWeight={600}>Daily Notes</Typography>
                                                    <Typography variant="caption" color="text.secondary">Work Performance · Delays · Extra Work</Typography>
                                                    {narrCount > 0 && (
                                                        <Chip size="small" color="success" label={narrCount} sx={{ height: 20 }} />
                                                    )}
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ pt: 0 }}>
                                                <SignDayNarrativesSection
                                                    weekDates={weekDates}
                                                    value={narr}
                                                    onChange={(next) => {
                                                        setNarrativesByCustomer(prev => ({ ...prev, [idx]: next }));
                                                    }}
                                                />
                                            </AccordionDetails>
                                        </Accordion>

                                        <Accordion disableGutters elevation={0} sx={accSx}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sumSx}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" fontWeight={600}>Foreman comment</Typography>
                                                    {commentFilled && (
                                                        <Chip size="small" variant="outlined" label="filled" sx={{ height: 20 }} />
                                                    )}
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ pt: 0 }}>
                                                <SignForemanCommentSection
                                                    comment={comment}
                                                    onCommentChange={(next) => {
                                                        setCommentByCustomer(prev => ({ ...prev, [idx]: next }));
                                                    }}
                                                />
                                            </AccordionDetails>
                                        </Accordion>
                                    </Box>
                                );
                            })()}

                            <Divider sx={{ my: 2 }} />

                            {/* ── Signatures: foreman (по умолчанию = залогиненный) + customer
                                   (опц.) в один ряд, компактно ── */}
                            {(() => {
                                const foremanSig = foremanSigByCustomer[currentCustomerIndex];
                                const customerSig = customerSigByCustomer[currentCustomerIndex];
                                const sigThumb = (src, alt) => (
                                    <Box sx={{
                                        height: 34, border: '1px solid #e2e8f0', borderRadius: 1,
                                        px: 0.5, bgcolor: '#fff', display: 'flex', alignItems: 'center',
                                    }}>
                                        <img src={src} alt={alt} style={{ maxHeight: 30, maxWidth: 110 }} />
                                    </Box>
                                );
                                return (
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                        {/* Foreman — по умолчанию залогиненный, можно сменить через пикер */}
                                        <Box sx={{ flex: 1, minWidth: 220 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                                                Foreman signature
                                            </Typography>
                                            <Box sx={{
                                                display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75,
                                                border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5,
                                            }}>
                                                <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                <Typography variant="body2" sx={{
                                                    flex: 1, minWidth: 0, fontWeight: 600,
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }}>
                                                    {foremanName || 'Select foreman…'}
                                                </Typography>
                                                <Button size="small" onClick={() => setForemanPickerOpen(true)} sx={{ px: 0.5, minWidth: 0 }}>
                                                    Change
                                                </Button>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Button size="small" variant="outlined" onClick={openForemanSignature} sx={{ borderRadius: 2 }}>
                                                    {foremanSig ? 'Redraw' : 'Draw signature'}
                                                </Button>
                                                {foremanSig && sigThumb(foremanSig, 'Foreman signature')}
                                            </Box>
                                        </Box>

                                        {/* Customer (optional) */}
                                        <Box sx={{ flex: 1, minWidth: 220 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                                                Customer signature <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
                                            </Typography>
                                            <TextField
                                                label="Customer Name"
                                                value={signerNameByCustomer[currentCustomerIndex] || ''}
                                                onChange={(e) => setSignerNameByCustomer(prev => ({ ...prev, [currentCustomerIndex]: e.target.value }))}
                                                fullWidth size="small" sx={{ mb: 0.75 }}
                                                placeholder="Customer representative…"
                                            />
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Button size="small" variant="outlined" onClick={openCustomerSignature} sx={{ borderRadius: 2 }}>
                                                    {customerSig ? 'Redraw' : 'Add signature'}
                                                </Button>
                                                {customerSig && sigThumb(customerSig, 'Customer signature')}
                                                {customerSig && (
                                                    <Button size="small" color="secondary" onClick={removeCustomerSignature}>Remove</Button>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            })()}

                            {/* Email me the PDF — письмо с фирменным PDF на email подписавшего */}
                            <FormControlLabel
                                sx={{ display: 'flex', mb: 0.5, ml: 0 }}
                                control={(
                                    <Checkbox
                                        size="small"
                                        checked={emailPdf}
                                        onChange={(e) => toggleEmailPdf(e.target.checked)}
                                    />
                                )}
                                label={(
                                    <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                                        Email me the signed PDF
                                    </Typography>
                                )}
                            />
                            {emailPdf && (
                                <Box sx={{ display: 'flex', gap: 0.75, mb: 1, ml: 4 }}>
                                    <Chip
                                        size="small"
                                        label={`Only ${dayTitle(signDay)}`}
                                        color={emailPdfScope === 'day' ? 'primary' : 'default'}
                                        variant={emailPdfScope === 'day' ? 'filled' : 'outlined'}
                                        onClick={() => pickEmailPdfScope('day')}
                                    />
                                    <Chip
                                        size="small"
                                        label="Whole week"
                                        color={emailPdfScope === 'week' ? 'primary' : 'default'}
                                        variant={emailPdfScope === 'week' ? 'filled' : 'outlined'}
                                        onClick={() => pickEmailPdfScope('week')}
                                    />
                                </Box>
                            )}

                            {/* Complete sign */}
                            <Button
                                onClick={handleComplete}
                                variant="contained"
                                color="success"
                                fullWidth
                                disabled={signing || !foremanName.trim()}
                                sx={{ borderRadius: 2, py: 1.5 }}
                            >
                                {signing ? 'Signing...' : `Sign ${dayTitle(signDay)}`}
                            </Button>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {mode === 'select' ? (
                        <>
                            <Button onClick={onClose}>Cancel</Button>
                            <Button
                                onClick={startSigning}
                                variant="contained"
                                color="success"
                                disabled={selectedJobIds.size === 0}
                            >
                                Sign ({selectedJobIds.size})
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={handleBack}>
                                Back
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, textAlign: 'center' }}>
                                {currentCustomerIndex + 1} of {frozenCustomers.length}
                            </Typography>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Signature pad modal — рисуем подпись форемана ИЛИ заказчика */}
            <SignatureModalWithAuto
                open={signatureModalOpen}
                onClose={() => { setSignatureModalOpen(false); setSigTarget(null); }}
                sigPadRef={sigPadRef}
                employeeName={sigTarget === 'foreman'
                    ? (foremanName || currentGroup?.customer || '')
                    : (signerNameByCustomer[currentCustomerIndex] || currentGroup?.customer || '')}
                existingSignature={sigTarget === 'foreman'
                    ? (foremanSigByCustomer[currentCustomerIndex] || null)
                    : (customerSigByCustomer[currentCustomerIndex] || null)}
                onAccept={handleSignatureAccept}
            />

            {/* Foreman signer picker — по умолчанию залогиненный, можно сменить (как в др. формах) */}
            <SelectEmployeesAndTempsModal
                open={foremanPickerOpen}
                onClose={() => setForemanPickerOpen(false)}
                onSelect={(selected) => {
                    const p = Array.isArray(selected) ? selected[0] : selected;
                    if (p) {
                        setForemanName(p.FullName || `${p.firstName || ''} ${p.lastName || ''}`.trim());
                    }
                    setForemanPickerOpen(false);
                }}
                singleSelect
                employeesOnly
            />
        </>
    );
}
